import { zipWith } from "lodash-es";
import { useId } from "react";
import * as V from "victory";

import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { isDefined } from "@/helpers/util";
import { useTranslation } from "@/translations";

import { useElementScale } from "../hooks/useElementScale";
import { chartTheme, dataColors } from "../theme";
import { splitLine } from "../util";
import { createDateTickFormat, createXAxis, createYAxis } from "./utility/Axis";
import { ChartPatterns } from "./utility/ChartPatterns";
import { HoverLabel } from "./utility/HoverLabel";

export interface BarChartDataPoint {
  x: string;
  y: number | undefined;
  isPending?: boolean;
}

type NonEmptyDataPoint<T extends BarChartDataPoint> = T & { y: NonNullable<T["y"]> };

interface ZippedData<TPrimaryDataPoint extends BarChartDataPoint, TSecondaryDataPoint extends BarChartDataPoint> {
  x: string;
  benchmark?: BarChartDataPoint;
  primary?: TPrimaryDataPoint;
  secondary?: TSecondaryDataPoint;
}

interface Props<TPrimaryDataPoint extends BarChartDataPoint, TSecondaryDataPoint extends BarChartDataPoint> {
  minY?: number;
  maxY?: number;
  type: "text" | "date";
  benchmark?: BarChartDataPoint[];
  renderLabel?: (
    dataPoint: ZippedData<TPrimaryDataPoint, TSecondaryDataPoint>,
    isSecondary: boolean,
  ) => React.ReactNode;
  formatXTick?: (y: NonEmptyDataPoint<TPrimaryDataPoint>["x"]) => string | number;
  formatYTick?: (y: NonEmptyDataPoint<TPrimaryDataPoint>["y"]) => string | number;
  xLabel?: string;
  yLabel?: string;
  skipXTicksModulo?: number;
  width?: number;
  height?: number;
  labelOrientation?: "horizontal" | "vertical";
  primaryData: {
    data: TPrimaryDataPoint[];
    color?: string;
    onClick?: (dataPoint: TPrimaryDataPoint) => void;
  };
  secondaryData?: {
    data: TSecondaryDataPoint[];
    color?: string;
    onClick?: (dataPoint: TSecondaryDataPoint) => void;
  };
}

const CHART_WIDTH = 400;
const CHART_HEIGHT = 350;
const LABEL_OFFSET_X = 12;
const LABEL_OFFSET_Y = -16;

export function BarChart<TPrimaryDataPoint extends BarChartDataPoint, TSecondaryDataPoint extends BarChartDataPoint>({
  minY = 0,
  maxY,
  type,
  benchmark,
  primaryData,
  secondaryData,
  formatYTick,
  formatXTick,
  renderLabel,
  xLabel,
  yLabel,
  skipXTicksModulo = 1,
  width = CHART_WIDTH,
  height = CHART_HEIGHT,
  labelOrientation = "horizontal",
}: Props<TPrimaryDataPoint, TSecondaryDataPoint>): React.ReactNode {
  const { scale, ref } = useElementScale<HTMLDivElement>(width);
  const { i18n } = useTranslation();

  const barWidth = secondaryData && secondaryData.data.length > 10 ? 12 : 16;
  const data: ZippedData<TPrimaryDataPoint, TSecondaryDataPoint>[] = zipWith(
    benchmark || [],
    primaryData.data,
    secondaryData?.data || [],
    (benchmarkDataPoint, primaryDataPoint, secondaryDataPoint) => ({
      x: benchmarkDataPoint ? benchmarkDataPoint.x : primaryDataPoint.x,
      benchmark: benchmarkDataPoint,
      primary: primaryDataPoint,
      secondary: secondaryDataPoint,
    }),
  );

  const getXAxisTickFormat = () => {
    if (type === "text") {
      return (x: NonEmptyDataPoint<TPrimaryDataPoint>["x"], idx: number) => {
        if (idx % skipXTicksModulo !== 0) {
          return " ";
        } else if (formatXTick) {
          return formatXTick(x);
        }

        return x;
      };
    } else {
      return createDateTickFormat(i18n, data.length, scale);
    }
  };

  const idPatternStripesPrimary = useId();
  const idPatternStripesSecondary = useId();
  const colorPrimaryData = primaryData.color || dataColors.primary;
  const colorSecondaryData = secondaryData?.color || dataColors.benchmark;
  const paddingRight = yLabel ? 8 : 0;
  const paddingBottom = xLabel ? 64 : 48;

  return (
    <div ref={ref}>
      <V.VictoryChart
        minDomain={{ y: minY }}
        maxDomain={{ y: maxY }}
        theme={chartTheme}
        width={scale * width}
        height={scale * height}
        padding={{
          top: 24,
          right: paddingRight,
          bottom: paddingBottom,
          left: 72,
        }}
        domainPadding={{ x: 32, y: [0, 140] }}
        categories={{ x: data.map((d) => d.x) }}
      >
        {createYAxis({ tickCount: 6, tickFormat: formatYTick, label: yLabel })}
        {createXAxis(
          {
            tickFormat: getXAxisTickFormat(),
            label: xLabel,
          },
          labelOrientation === "vertical"
            ? {
                dx: -38,
                dy: -8,
                angle: -90,
                textAnchor: "middle",
              }
            : undefined,
        )}

        {/* Stripped pattern svg, used for live data bar */}
        <ChartPatterns.Stripes id={idPatternStripesPrimary} color={colorPrimaryData} />
        <ChartPatterns.Stripes id={idPatternStripesSecondary} color={colorSecondaryData} />

        <V.VictoryGroup offset={barWidth * scale}>
          {/* Primary data */}
          <V.VictoryBar
            events={[
              {
                target: "data",
                eventHandlers: {
                  onClick: () => [
                    {
                      mutation: (props: V.CallbackArgs) => {
                        primaryData.onClick?.(props.datum.primary);
                      },
                    },
                  ],
                },
              },
            ]}
            barWidth={barWidth * scale}
            style={{
              data: {
                cursor: primaryData.onClick ? "pointer" : "default",
                fill: (tick: V.CallbackArgs) => {
                  // Show different bar fill if the data point is in progress
                  const isDataPointPending = tick.datum.primary.isPending;

                  return isDataPointPending ? `url(#${idPatternStripesPrimary})` : colorPrimaryData;
                },
                strokeWidth: 0,
              },
            }}
            labelComponent={
              <V.VictoryTooltip
                text=""
                flyoutComponent={
                  <HoverLabel<(typeof data)[number]>
                    offsetX={LABEL_OFFSET_X * scale}
                    offsetY={LABEL_OFFSET_Y * scale}
                    containerWidth={width * scale}
                  >
                    {(dataPoint) => (
                      <>
                        {type === "text" ? null : <FormattedDate format="monthYear" date={dataPoint.x} />}
                        {renderLabel?.(dataPoint, false)}
                      </>
                    )}
                  </HoverLabel>
                }
              />
            }
            labels={(d) => d}
            y={(d: (typeof data)[number]) => d.primary?.y || 0}
            data={data.map((d) => (isDefined(d.primary?.y) ? d : undefined)).filter(isDefined)}
          />
          {/* Secondary data */}
          {secondaryData ? (
            <V.VictoryBar
              events={[
                {
                  target: "data",
                  eventHandlers: {
                    onClick: () => [
                      {
                        mutation: (props: V.CallbackArgs) => {
                          secondaryData.onClick?.(props.datum.secondary);
                        },
                      },
                    ],
                  },
                },
              ]}
              barWidth={barWidth * scale}
              style={{
                data: {
                  cursor: primaryData.onClick ? "pointer" : "default",
                  fill: (tick: V.CallbackArgs) => {
                    // Show different bar fill if the data point is in progress
                    const isDataPointPending = tick.datum.primary.isPending;

                    return isDataPointPending ? `url(#${idPatternStripesSecondary})` : colorSecondaryData;
                  },
                  strokeWidth: 0,
                },
              }}
              labelComponent={
                <V.VictoryTooltip
                  text=""
                  flyoutComponent={
                    <HoverLabel<(typeof data)[number]>
                      offsetX={LABEL_OFFSET_X * scale}
                      offsetY={LABEL_OFFSET_Y * scale}
                      containerWidth={width * scale}
                    >
                      {(dataPoint) => (
                        <>
                          {type === "text" ? null : <FormattedDate format="monthYear" date={dataPoint.x} />}
                          {renderLabel?.(dataPoint, true)}
                        </>
                      )}
                    </HoverLabel>
                  }
                />
              }
              labels={(d) => d}
              y={(d: (typeof data)[number]) => d.secondary?.y || 0}
              data={data.map((d) => (isDefined(d.secondary?.y) ? d : undefined)).filter(isDefined)}
            />
          ) : null}
        </V.VictoryGroup>
        {benchmark ? (
          <V.VictoryGroup>
            {splitLine(benchmark, (d) => d.y).map((line) => (
              <V.VictoryLine
                key={JSON.stringify(line)}
                style={{ data: { pointerEvents: "none", strokeWidth: 2, stroke: dataColors.benchmark } }}
                data={line.map((d) => (isDefined(d?.y) ? d : undefined)).filter(isDefined)}
                labels={[]}
              />
            ))}
          </V.VictoryGroup>
        ) : null}
      </V.VictoryChart>
    </div>
  );
}
